import React, { useState, useEffect, useContext, useMemo } from "react";
import { Calendar, Clock, Edit, Plus, Filter, X, Search } from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import { Pencil, Trash2 } from "lucide-react";
import AppointmentModal from "./AppoinmentModel";
import { Link } from "react-router-dom";

const MyAppointment = () => {
  const {
    loginUser,
    getPetAppointment,
    appointments,
    appointmentLoading,
    appointmentError,
  } = useContext(AuthContext);

  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    type: "all",
    pet: "all",
    doctor: "all",
    dateRange: "all", // all, upcoming, past, today, this_week, this_month
    searchTerm: "",
  });

  const [showFilters, setShowFilters] = useState(false);

  const handleBookNow = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAppointmentSuccess = () => {
    console.log("Appointment created successfully!");
  };

  // Fetch appointment data when component mounts
  useEffect(() => {
    if (appointments === null) {
      getPetAppointment();
    }
  }, [getPetAppointment, appointments]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    if (!appointments || appointments.length === 0) {
      return {
        statuses: [],
        types: [],
        pets: [],
        doctors: [],
      };
    }

    return {
      statuses: [...new Set(appointments.map((apt) => apt.status))],
      types: [...new Set(appointments.map((apt) => apt.type))],
      pets: [
        ...new Set(appointments.map((apt) => apt.pets?.name).filter(Boolean)),
      ],
      doctors: [
        ...new Set(appointments.map((apt) => apt.doctor?.name).filter(Boolean)),
      ],
    };
  }, [appointments]);

  // Filter appointments based on current filters
  const filteredAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    return appointments.filter((appointment) => {
      const appointmentDate = new Date(appointment.date);
      const today = new Date();
      const startOfToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      const endOfToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59
      );

      // Status filter
      if (filters.status !== "all" && appointment.status !== filters.status) {
        return false;
      }

      // Type filter
      if (filters.type !== "all" && appointment.type !== filters.type) {
        return false;
      }

      // Pet filter
      if (filters.pet !== "all" && appointment.pets?.name !== filters.pet) {
        return false;
      }

      // Doctor filter
      if (
        filters.doctor !== "all" &&
        appointment.doctor?.name !== filters.doctor
      ) {
        return false;
      }

      // Date range filter
      if (filters.dateRange !== "all") {
        switch (filters.dateRange) {
          case "today":
            if (
              appointmentDate < startOfToday ||
              appointmentDate > endOfToday
            ) {
              return false;
            }
            break;
          case "upcoming":
            if (appointmentDate < startOfToday) {
              return false;
            }
            break;
          case "past":
            if (appointmentDate >= startOfToday) {
              return false;
            }
            break;
          case "this_week":
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startOfWeek.setHours(0, 0, 0, 0);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            endOfWeek.setHours(23, 59, 59, 999);
            if (appointmentDate < startOfWeek || appointmentDate > endOfWeek) {
              return false;
            }
            break;
          case "this_month":
            const startOfMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              1
            );
            const endOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              0,
              23,
              59,
              59
            );
            if (
              appointmentDate < startOfMonth ||
              appointmentDate > endOfMonth
            ) {
              return false;
            }
            break;
        }
      }

      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const searchableText = [
          appointment.type,
          appointment.pets?.name,
          appointment.doctor?.name,
          appointment.notes,
          appointment.status,
        ]
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [appointments, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: "all",
      type: "all",
      pet: "all",
      doctor: "all",
      dateRange: "all",
      searchTerm: "",
    });
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== "all" && value !== ""
  );

  // Loading state
  if (appointmentLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          My Appointments
          {console.log("appointments: ", appointments)}
        </h2>
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading appointments...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg py-6">
      {/* Professional Header */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex flex-row sm:flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title Section */}
          <div className="flex items-center space-x-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                My Appointments
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Manage your upcoming and past appointments
              </p>
            </div>
          </div>

          {/* Action Section */}
          <div className="flex items-center space-x-3">
            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${showFilters || hasActiveFilters
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Filters</span>
              {hasActiveFilters && (
                <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                  {
                    Object.values(filters).filter(
                      (value) => value !== "all" && value !== ""
                    ).length
                  }
                </span>
              )}
            </button>

            {/* Book Appointment Button */}
            <Link
              to="/dashboard/create-appointment"
              onClick={handleBookNow}
              className="inline-flex items-center px-2.5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:shadow-md"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Book New Appointment</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Filter Appointments
            </h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Search */}
            <div className="xl:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search appointments..."
                  value={filters.searchTerm}
                  onChange={(e) =>
                    handleFilterChange("searchTerm", e.target.value)
                  }
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                {filterOptions.statuses.map((status) => (
                  <option key={status} value={status} className="capitalize">
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {filterOptions.types.map((type) => (
                  <option key={type} value={type} className="capitalize">
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Pet Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pet
              </label>
              <select
                value={filters.pet}
                onChange={(e) => handleFilterChange("pet", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Pets</option>
                {filterOptions.pets.map((pet) => (
                  <option key={pet} value={pet}>
                    {pet}
                  </option>
                ))}
              </select>
            </div>

            {/* Doctor Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Doctor
              </label>
              <select
                value={filters.doctor}
                onChange={(e) => handleFilterChange("doctor", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Doctors</option>
                {filterOptions.doctors.map((doctor) => (
                  <option key={doctor} value={doctor}>
                    {doctor}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) =>
                  handleFilterChange("dateRange", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Results Summary */}
      {appointments && appointments.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredAppointments.length} of {appointments.length}{" "}
            appointments
            {hasActiveFilters && " (filtered)"}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
          <div className="flex justify-between items-center">
            <span>{error}</span>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Appointments List */}
      {filteredAppointments && filteredAppointments.length > 0 ? (
        <div className="grid sm:grid-cols-3 grid-cols-1 gap-4 pr-2">
          {filteredAppointments.map((appointment) => (
            <div
              key={appointment._id}
              className="shadow-sm bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full"
            >

              <div className="flex items-start justify-between flex-1">
                <div className="flex-1 min-w-0 pr-3">
                  <h3 className="font-semibold text-gray-900 capitalize text-base leading-tight mb-2">
                    {appointment.type} ({appointment.pets.name})
                  </h3>

                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        {formatDate(appointment.date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{appointment.time}</span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-2 truncate">
                    {appointment.doctor.name}
                  </p>

                  {appointment.notes && (
                    <p className="text-sm text-gray-500 italic line-clamp-2 overflow-hidden">
                      {appointment.notes}
                    </p>
                  )}
                  {appointment.meetLink && appointment.status === "confirmed" && <button
                    onClick={() => window.open(appointment.meetLink, '_blank')}
                    className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Join Meeting
                  </button>}
                </div>

                <div className="flex flex-col items-end justify-start space-y-2 flex-shrink-0">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getStatusColor(
                      appointment.status
                    )}`}
                  >
                    {appointment.status}
                  </span>

                  <div className="flex justify-center space-x-2">
                    {appointment.status !== "completed" &&
                      appointment.status !== "cancelled" && (
                        <>
                          {/* <button className="text-blue-600 hover:text-blue-800 text-sm p-1">
                    <Edit size={16} />
                  </button>
                  <button className="text-red-600 hover:text-red-800 text-sm p-1">
                    <Trash2 size={16} />
                  </button> */}
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : appointments && appointments.length > 0 ? (
        // No results found after filtering
        <div className="text-center py-8">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No appointments match your filters
          </h3>
          <p className="text-gray-600 mb-4">
            Try adjusting your filter criteria or clearing all filters.
          </p>
          <button
            onClick={clearFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        // No appointments at all
        <div className="text-center py-8">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No appointments scheduled
          </h3>
          <p className="text-gray-600 mb-4">
            Book your first appointment to get started!
          </p>
          {/* <button
            onClick={handleBookNow}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
          >
            <Calendar className="w-5 h-5" />
            <span>Book Appointment</span>
          </button> */}
        </div>
      )}
    </div>
  );
};

export default MyAppointment;
